import {
  Application,
  MeshRope,
  Point,
  Sprite,
  Texture,
} from "pixi.js";
import { AssetKeys } from "../game/Assets";
import { bakeRotatedTexture } from "../game/PixiUtils";

const PEND_GRAVITY = 1500;
const PEND_DAMPING = 0.06;
const ROPE_THICKNESS = 28;
const ROPE_SEGMENTS = 18;
const ROPE_GRAVITY = 1400;
const ROPE_DAMPING = 0.985;
const ROPE_CONSTRAINT_PASSES = 4;
const MONKEY_SPRITE_SCALE = 0.2;
const VINE_LENGTH_FRACTION = 0.4;
const INITIAL_SWING_ANGLE = -0.28;
const PIVOT_X_FRACTION = 0.35;

interface RopePoint {
  x: number;
  y: number;
  ox: number;
  oy: number;
}

export class HomeMonkey {
  private app = new Application();
  private monkey!: Sprite;
  private mesh!: MeshRope;
  private rope: RopePoint[] = [];
  private meshPoints: Point[] = [];

  private pivotX = 0;
  private pivotY = 0;
  private vineLength = 0;
  private segmentLen = 0;

  private pendAngle = INITIAL_SWING_ANGLE;
  private pendOmega = 0;

  private mounted = false;

  async mount(host: HTMLElement, textures: Record<string, Texture>) {
    if (this.mounted) return;
    const rect = host.getBoundingClientRect();
    const width = Math.max(Math.round(rect.width) || 400, 200);
    const height = Math.max(Math.round(rect.height) || 700, 200);

    await this.app.init({
      width,
      height,
      backgroundAlpha: 0,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });
    const canvas = this.app.canvas;
    canvas.style.position = "absolute";
    canvas.style.inset = "0";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.display = "block";
    host.appendChild(canvas);
    this.mounted = true;

    const monkeyTex = textures[AssetKeys.BenjiHold];
    const vineTex = textures[AssetKeys.Vine];
    if (!monkeyTex || !vineTex) {
      console.warn("HomeMonkey: missing textures", { monkeyTex, vineTex });
      throw new Error("HomeMonkey: textures not provided");
    }

    const rotatedVine = bakeRotatedTexture(this.app.renderer, vineTex);

    this.pivotX = width * PIVOT_X_FRACTION;
    this.pivotY = 0;
    this.vineLength = height * VINE_LENGTH_FRACTION;
    this.segmentLen = this.vineLength / (ROPE_SEGMENTS - 1);

    for (let i = 0; i < ROPE_SEGMENTS; i++) {
      const y = this.pivotY + i * this.segmentLen;
      this.rope.push({ x: this.pivotX, y, ox: this.pivotX, oy: y });
      this.meshPoints.push(new Point(this.pivotX, y));
    }

    this.mesh = new MeshRope({
      texture: rotatedVine,
      points: this.meshPoints,
      width: ROPE_THICKNESS,
    });
    this.app.stage.addChild(this.mesh);

    this.monkey = new Sprite(monkeyTex);
    this.monkey.anchor.set(0.85, 0.5);
    this.monkey.scale.x = -MONKEY_SPRITE_SCALE;
    this.monkey.scale.y = MONKEY_SPRITE_SCALE;
    this.app.stage.addChild(this.monkey);

    for (let i = 0; i < 50; i++) this.step(1 / 60);

    this.app.ticker.add((t) => {
      const dt = Math.min(t.deltaMS / 1000, 1 / 30);
      this.step(dt);
    });
  }

  private step(dt: number) {
    const accel =
      -(PEND_GRAVITY / this.vineLength) * Math.sin(this.pendAngle) -
      PEND_DAMPING * this.pendOmega;
    this.pendOmega += accel * dt;
    this.pendAngle += this.pendOmega * dt;

    const tipX = this.pivotX + this.vineLength * Math.sin(this.pendAngle);
    const tipY = this.pivotY + this.vineLength * Math.cos(this.pendAngle);

    this.monkey.position.set(tipX, tipY);
    this.monkey.rotation = -this.pendAngle;

    const last = this.rope.length - 1;
    for (let i = 1; i < last; i++) {
      const p = this.rope[i];
      const vx = (p.x - p.ox) * ROPE_DAMPING;
      const vy = (p.y - p.oy) * ROPE_DAMPING;
      p.ox = p.x;
      p.oy = p.y;
      p.x += vx;
      p.y += vy + ROPE_GRAVITY * dt * dt;
    }
    this.rope[0].x = this.pivotX;
    this.rope[0].y = this.pivotY;
    this.rope[last].x = tipX;
    this.rope[last].y = tipY;

    for (let pass = 0; pass < ROPE_CONSTRAINT_PASSES; pass++) {
      for (let i = 0; i < last; i++) {
        const a = this.rope[i];
        const b = this.rope[i + 1];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.hypot(dx, dy) || 1;
        const diff = (dist - this.segmentLen) / dist;
        const fx = dx * diff;
        const fy = dy * diff;
        const aFixed = i === 0;
        const bFixed = i === last - 1;
        if (!aFixed && !bFixed) {
          a.x += fx * 0.5;
          a.y += fy * 0.5;
          b.x -= fx * 0.5;
          b.y -= fy * 0.5;
        } else if (aFixed && !bFixed) {
          b.x -= fx;
          b.y -= fy;
        } else if (!aFixed && bFixed) {
          a.x += fx;
          a.y += fy;
        }
      }
    }

    for (let i = 0; i <= last; i++) {
      this.meshPoints[i].set(this.rope[i].x, this.rope[i].y);
    }
  }

  destroy() {
    if (!this.mounted) return;
    this.app.destroy(true, { children: true });
    this.mounted = false;
  }
}
